import type { TemplateResult } from 'lit';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LocalDate } from 'src/utilities/local-date.js';
import '../../content/issues/d-external-report-item.js';
import '../../library/editors/index.js';
import '../../library/editors/elements/d-edit-number';
import '../../library/editors/elements/d-edit-text';
import { EntityContent } from '../entity-content.js';
import './d-issue-behavior.js';
import './issue-messages/d-issue-messages-section.js';
import { isEmptyOrInvalidString } from 'src/utilities/text';
import { descriptionForExternal } from 'src/content/issues/d-issue-behavior';
import { t } from 'src/i18n';
import type { UpdateSectionItem } from '../update-section-item';
import type { ListDefinition } from '../list-definition';
import type { IssueViewEditItem } from './issue-view-edit-item';
import type { IssueView, IssueDeadlineExtensionItem } from './issue-view';
import type { SectionField } from '../../library/components/section-field';

/**
 *
 */
@customElement('d-issue-view')
export class DIssueView extends EntityContent<IssueView, IssueViewEditItem> {
  static readonly styles = [
    ...EntityContent.styles,
    css`
      .status {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        border-left: 1px solid var(--borderColor);
        border-right: 1px solid var(--borderColor);
        border-bottom: 1px solid var(--borderColor);
        padding: 15px 12px 14px 48px;
        background: hsl(0, 0%, 96%) url(/images/alert.svg) 12px 9px no-repeat;
        background-size: 34px;
      }
      .processed {
        background: hsl(0, 0%, 96%) url(/images/check-mini-blue.svg) 12px 9px no-repeat;
        background-size: 34px;
      }
      .status d-label {
        margin-right: 8px;
      }
      .status d-action {
        margin: -6px -8px;
      }
      .report {
        margin-top: 12px;
        margin-bottom: -1px;
        border: 1px solid var(--borderColor);
        background: hsl(0, 0%, 96%);
        padding: 0 20px;
        overflow: hidden;
      }
      .report > * {
        margin-top: -2px;
      }
      .process {
        overflow: hidden;
      }
      .process > * {
        margin-top: -1px;
      }
      .deadline {
        margin-top: 12px;
        border: 1px solid var(--borderColor);
        background: hsl(0, 0%, 96%);
        padding: 12px 20px;
      }
      .deadline.overdue {
        background: hsl(5, 75%, 97%);
      }
      .deadline-main {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 4px 12px;
      }
      .deadline-info {
        display: flex;
        flex: 1;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 4px 10px;
      }
      .deadline-info d-label {
        font-weight: bold;
      }
      .deadline-date {
        font-weight: bold;
      }
      .deadline-relative {
        color: var(--themeColorDarkerThree, #555);
      }
      .deadline.overdue .deadline-relative {
        color: var(--alertColor, #c00);
      }
      .deadline-form {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-end;
        gap: 8px;
        margin-top: 10px;
      }
      .deadline-form d-edit-number {
        max-width: 130px;
      }
      .deadline-form d-edit-text {
        flex: 1;
        min-width: 160px;
      }
      .deadline-history {
        margin-top: 8px;
      }
      ul.deadline-history-list {
        margin: 8px 0 0 0;
        padding: 0;
        list-style: none;
      }
      ul.deadline-history-list li {
        display: flex;
        flex-wrap: wrap;
        gap: 2px 10px;
        padding: 6px 0;
        border-top: 1px solid var(--borderColorLight);
        font-size: 14px;
      }
      .deadline-history-dates {
        font-weight: bold;
      }
      .deadline-history-reason,
      .deadline-history-meta {
        color: var(--themeColorDarkerThree, #555);
      }
    `,
  ];
  @property({ type: String })
  entityType = 'issues';
  @property({ type: String })
  itemReportedTo = '';
  @property({ type: String })
  itemProcessedBy = '';
  @property({ type: Array })
  employees = [];
  @property({ type: String })
  itemEvaluatedBy = '';
  @property({ type: Boolean })
  dentist = false;
  @property({ type: Object })
  fieldsByCode = {};
  @property({ type: String })
  informationLeakagePossibilityTooltipSimplified = 'undefined';
  @property({ type: String })
  externalRecipient = '';
  @property({
    type: Boolean,
  })
  pllParticipant = false;
  @property({
    type: Array,
  })
  externalRecipients = [];
  @state()
  private extending = false;
  @state()
  private extendDays = 7;
  @state()
  private extendReason = '';
  @state()
  private showDeadlineHistory = false;

  mainHeaderActions() {
    return super.mainHeaderActions().map((action) => {
      if (action.action === 'edit') {
        action.name = t('issues.processAction');
      }
      return action;
    });
  }
  _externalReports(issue) {
    const l = issue.externalIssueMessages ?? [];
    return l.map((x) => {
      return {
        created: x.timestamp + ' 12:00:00',
        senderName: '',
        recipientName: x.recipientName,
        subject: t('issues.externalReportSubjectUnavailable'),
        description: t('issues.externalReportDescriptionRequiresLogin'),
      };
    });
  }

  _sendExternalReport(e) {
    if (e.target.hasAttribute('disabled')) {
      return;
    }
    const fullDescription = descriptionForExternal(
      this.entityView.description,
      this.entityView.eventDate,
      this.entityView.immediateMeasures,
      this.entityView.furtherMeasures,
    );
    const returnUrl = window.location.href;
    window.location.href =
      'https://avvik.trinnvis.io/?issue-id=' +
      this.entityView.uuid +
      '&public-recipient=' +
      this.externalRecipient +
      '&public-subject=' +
      encodeURI(this.entityView.name) +
      '&public-description=' +
      encodeURI(fullDescription) +
      '&return-url=' +
      returnUrl;
  }

  _dateFormat(value) {
    return value ? LocalDate.fromString(value).toStringForDisplay() : '';
  }

  ready() {
    this._fetchRecipients();

    if (
      location.href.startsWith('https://app.trinnvis.no/account/2668') ||
      location.href.startsWith('https://app.trinnvis.no/account/971')
    ) {
      this.pllParticipant = true;
    }
  }

  _fetchRecipients() {
    fetch('https://api.trinnvis.io/external-issues/recipients/')
      .then((response) => response.text())
      .then((recipients) => {
        const list = JSON.parse(recipients);
        const options = list.map((item) => ({
          uuid: item.id,
          value: item.description,
        }));
        this.externalRecipients = options;
      });
  }

  formatAsSentTime(date) {
    if (date === undefined) {
      return '';
    }
    const localDate = LocalDate.fromString(date.substring(0, 10));
    const dateAsString = localDate.toStringForDisplayWithDayOfWeekAndYear();
    const timePartHours = date.substring(11, 13);
    const timePartMinutes = date.substring(14, 16);
    const timeAsString = t('issues.sentTimePrefix') + timePartHours + '.' + timePartMinutes;
    return dateAsString + ' ' + timeAsString;
  }

  capitalize(text) {
    return text.toLowerCase().replace(/\w\S*/g, (w) => w.replace(/^\w/, (c) => c.toUpperCase()));
  }

  async initializeEditItem() {
    this.editItem = {
      reportedDate: this.entityView.reportedDate,
      reportedBy: this.entityView.reportedBy,
      reportedTo: this.entityView.reportedTo,
      description: this.entityView.description,
      eventDate: this.entityView.eventDate,
      immediateMeasures: this.entityView.immediateMeasures,
      furtherMeasures: this.entityView.furtherMeasures,
      /**
       * Processing
       */
      name: this.entityView.name,
      processedDate: this.entityView.processedDate,
      processedBy: this.entityView.processedBy,
      notes: this.entityView.notes,
      /**
       * Checklist
       */
      personDamagePossibility: this.entityView.personDamagePossibility,
      relateToPatient: this.entityView.relateToPatient,
      relateToEmployees: this.entityView.relateToEmployees,
      workRelatedHealthIssue: this.entityView.workRelatedHealthIssue,
      relateToEquipment: this.entityView.relateToEquipment,
      relateToRadiation: this.entityView.relateToRadiation,
      informationLeakagePossibility: this.entityView.informationLeakagePossibility,
      relateToMaritimeHealthCertificate: this.entityView.relateToMaritimeHealthCertificate,
      relateToArtificialIntelligence: this.entityView.relateToArtificialIntelligence,
      /**
       * Work related health issue
       */
      workRelatedHealthIssueEmployee: this.entityView.workRelatedHealthIssueEmployee,
      workRelatedHealthIssueDescription: this.entityView.workRelatedHealthIssueDescription,
      workRelatedInjury: this.entityView.workRelatedInjury,
      workRelatedSickness: this.entityView.workRelatedSickness,
      workRelatedInjuryTask: this.entityView.workRelatedInjuryTask,
      workRelatedInjuryIncidentCause: this.entityView.workRelatedInjuryIncidentCause,
      workRelatedSicknessCausingTask: this.entityView.workRelatedSicknessCausingTask,
      workRelatedSicknessCausingTaskLocation: this.entityView.workRelatedSicknessCausingTaskLocation,
      workRelatedSicknessCausingEnvironment: this.entityView.workRelatedSicknessCausingEnvironment,
      /**
       * Measures
       */
      requiresMeasures: this.entityView.requiresMeasures,
      identifiedMeasures: this.entityView.identifiedMeasures,
      implementedMeasure: this.entityView.implementedMeasure,
      measuresSatisfactorily: this.entityView.measuresSatisfactorily,
      /**
       * Evaluation
       */
      evaluated: this.entityView.evaluated,
      evaluatedDate: this.entityView.evaluatedDate,
      evaluatedBy: this.entityView.evaluatedBy,
      evaluatedOk: this.entityView.evaluatedOk,
      /**
       * Meta data and ui resources
       */
      type: 'issues',
      uuid: this.entityView.uuid,
      pages: this.entityView.pages,
      classification: this.entityView.classification,
      accessControl: this.entityView.accessControl,
    };
  }

  viewFieldClassification(): SectionField[] {
    return [
      {
        field: 'classificationText',
        fieldForLabel: 'generalFields_access',
        type: 'view-text',
        class: 'fullWidth',
        condition: this.entityView.classificationText !== '',
      },
    ];
  }

  viewFieldsReport(editMode: boolean = false): SectionField[] {
    return [
      {
        field: 'report',
        sublabel: editMode ? t('issues.tableNotEditable') : '',
        type: 'header',
        size: 'verybig',
      },
      {
        field: 'reportedDate',
        type: 'view-date',
        format: 'date-short',
        class: 'minWidth200',
        condition: true,
      },
      {
        field: 'reportedByName',
        fieldForLabel: 'reportedBy',
        type: 'view-text',
        class: 'minWidth200',
      },
      {
        field: 'reportedToName',
        fieldForLabel: 'reportedTo',
        type: 'view-text',
        class: 'minWidth200',
      },
      {
        field: 'description',
        type: 'view-text',
        class: 'fullWidth',
      },
      {
        field: 'eventDate',
        type: 'view-date',
        format: 'date-short',
        class: 'minWidth200',
        hideIfEmpty: true,
      },
      {
        field: 'immediateMeasures',
        type: 'view-text',
        class: 'fullWidth',
        hideIfEmpty: true,
      },
      {
        field: 'furtherMeasures',
        type: 'view-text',
        class: 'fullWidth',
        hideIfEmpty: true,
      },
    ];
  }

  viewFieldsProcessAnalysis(): SectionField[] {
    return [
      {
        field: 'process',
        type: 'header',
        size: 'verybig',
      },
      {
        field: 'notes',
        type: 'view-text',
        class: 'fullWidth',
      },
      {
        field: 'processedDate',
        type: 'view-date',
        format: 'date-short',
        class: 'minWidth200',
      },
      {
        field: 'processedByName',
        type: 'view-text',
        class: 'minWidth200',
      },
      {
        field: 'personalDamage',
        type: 'view-conditional-bullet-list',
        condition: this.entityView.personDamagePossibility,
        listItems: [
          {
            field: 'personDamagePossibility',
          },
          {
            field: 'relateToPatient',
          },
          {
            field: 'relateToEmployees',
          },
          {
            field: 'workRelatedHealthIssue',
          },
        ],
      },
      {
        field: 'workRelatedHealthIssueTitle',
        type: 'header',
        condition: this.entityView.workRelatedHealthIssue,
      },
      {
        field: 'workRelatedHealthIssueType',
        type: 'view-text',
        class: 'fullWidth',
        condition: this.entityView.workRelatedHealthIssue,
      },
      {
        field: 'workRelatedHealthIssueDescription',
        type: 'view-text',
        class: 'fullWidth',
        condition: this.entityView.workRelatedHealthIssue,
      },
      {
        field: 'workRelatedInjuryTask',
        type: 'view-text',
        class: 'fullWidth',
        condition: this.entityView.workRelatedInjury,
      },
      {
        field: 'workRelatedInjuryIncidentCause',
        type: 'view-text',
        class: 'fullWidth',
        condition: this.entityView.workRelatedInjury,
      },
      {
        field: 'equipmentTitle',
        type: 'view-conditional-bullet-list',
        condition: this.entityView.relateToEquipment || this.entityView.relateToRadiation,
        listItems: [
          {
            field: 'relateToEquipment',
          },
          {
            field: 'relateToRadiation',
          },
        ],
      },
      {
        field: 'personalInformationTitle',
        type: 'view-conditional-bullet-list',
        condition: this.entityView.informationLeakagePossibility,
        listItems: [
          {
            field: 'informationLeakagePossibility',
          },
        ],
      },
      {
        field: 'maritimeDoctorTitle',
        type: 'view-conditional-bullet-list',
        condition: this.entityView.relateToMaritimeHealthCertificate && this.entityView.maritimeDoctor,
        listItems: [
          {
            field: 'relateToMaritimeHealthCertificate',
          },
        ],
      },
      {
        field: 'relateToArtificialIntelligence',
        label: t('issues.aiLabel'),
        type: 'view-text',
        class: 'fullWidth',
        condition: this.entityView.relateToArtificialIntelligence,
        value: () => t('issues.aiRelatedIssue'),
      },
    ];
  }

  viewFieldsProcessMeasures(): SectionField[] {
    return [
      {
        field: 'measuresTitle',
        type: 'header',
      },
      {
        field: '',
        type: 'view-conditional-bullet-list',
        condition: true,
        listItems: [
          {
            field: 'requiresMeasures',
            elseField: 'requiresMeasuresNOT',
            hideIfNull: true,
          },
        ],
      },
      {
        field: 'identifiedMeasures',
        type: 'view-text',
        noValueValue: t('issues.noMeasuresDecided'),
        class: 'fullWidth',
        condition: this.entityView.requiresMeasures,
      },
      {
        field: '',
        type: 'view-conditional-bullet-list',
        condition: !isEmptyOrInvalidString(this.entityView.identifiedMeasures),
        listItems: [
          {
            field: 'implementedMeasure',
            elseField: 'implementedMeasureNOT',
            hideIfNull: true,
          },
          {
            field: 'evaluated',
            elseField: 'evaluatedNOT',
            hideIfNull: true,
          },
        ],
      },
      {
        field: 'evaluatedDate',
        type: 'view-date',
        format: 'date-short',
        class: 'minWidth200',
        condition: this.entityView.evaluated,
      },
      {
        field: 'evaluatedByName',
        type: 'view-text',
        class: 'minWidth200',
        condition: this.entityView.evaluated,
      },
      {
        field: '',
        type: 'view-conditional-bullet-list',
        condition: this.entityView.evaluated,
        listItems: [
          {
            field: 'evaluatedOk',
            elseField: 'evaluatedOkNOT',
            hideIfNull: true,
          },
        ],
      },
    ];
  }

  private deadlineRelativeText(deadline: string): string {
    const today = LocalDate.now();
    const dl = LocalDate.fromString(deadline);
    const daysUntil = today.until(dl);
    if (daysUntil > 0) {
      return t('issues.deadline.daysLeft', { count: daysUntil });
    }
    if (daysUntil === 0) {
      return t('issues.deadline.dueToday');
    }
    return t('issues.escalation.daysOverdue', { count: dl.until(today) });
  }

  private confirmExtendDeadline() {
    const base = this.entityView.deadline ? LocalDate.fromString(this.entityView.deadline) : LocalDate.now();
    const newDeadline = base.plusDays(this.extendDays).toString();
    this.dispatchEvent(
      new CustomEvent('extend-issue-deadline', {
        bubbles: true,
        composed: true,
        detail: { uuid: this.entityView.uuid, deadline: newDeadline, reason: this.extendReason.trim() },
      }),
    );
    this.extending = false;
    this.extendReason = '';
    this.extendDays = 7;
  }

  private renderExtendForm() {
    return html`
      <div class="deadline-form">
        <d-edit-number
          label=${t('issues.escalation.snoozeDaysLabel')}
          .min=${1}
          .value=${this.extendDays}
          @value-changed=${(e: CustomEvent) => (this.extendDays = e.detail.value)}
        ></d-edit-number>
        <d-edit-text
          label=${t('issues.escalation.snoozeReasonLabel')}
          placeholder=${t('issues.escalation.snoozeReasonPlaceholder')}
          .value=${this.extendReason}
          @value-changed=${(e: CustomEvent) => (this.extendReason = e.detail.value)}
        ></d-edit-text>
        <d-action @click=${() => this.confirmExtendDeadline()} ?disabled=${this.extendReason.trim() === ''}
          >${t('common:save')}</d-action
        >
        <d-action @click=${() => (this.extending = false)}>${t('common:cancel')}</d-action>
      </div>
    `;
  }

  private renderDeadlineHistory(extensions: IssueDeadlineExtensionItem[]) {
    return html`
      <div class="deadline-history">
        <d-action plain @click=${() => (this.showDeadlineHistory = !this.showDeadlineHistory)}
          >${t('issues.deadline.historyToggle', { count: extensions.length })}</d-action
        >
        ${this.showDeadlineHistory
          ? html`<ul class="deadline-history-list">
              ${extensions.map(
                (x) =>
                  html`<li>
                    <span class="deadline-history-dates"
                      >${x.previousDeadline ? LocalDate.fromString(x.previousDeadline).toStringForDisplay() : ''} →
                      ${x.newDeadline ? LocalDate.fromString(x.newDeadline).toStringForDisplay() : ''}</span
                    >
                    ${x.reason ? html`<span class="deadline-history-reason">${x.reason}</span>` : nothing}
                    <span class="deadline-history-meta"
                      >${x.changedByName ? t('issues.deadline.changedBy', { name: x.changedByName }) : ''}
                      ${x.changedAt ? this._dateFormat(x.changedAt.substring(0, 10)) : ''}</span
                    >
                  </li>`,
              )}
            </ul>`
          : nothing}
      </div>
    `;
  }

  private renderDeadlineSection() {
    const deadline = this.entityView.deadline;
    if (!deadline) {
      return nothing;
    }
    const extensions = this.entityView.deadlineExtensions ?? [];
    const overdue = LocalDate.fromString(deadline).isBefore(LocalDate.now());
    return html`
      <div class="deadline ${overdue ? 'overdue' : ''}">
        <div class="deadline-main">
          <div class="deadline-info">
            <d-label .label=${t('issues.deadline.current')}></d-label>
            <span class="deadline-date">${LocalDate.fromString(deadline).toStringForDisplay()}</span>
            <span class="deadline-relative">${this.deadlineRelativeText(deadline)}</span>
          </div>
          ${!this.entityView.processed && !this.hideEdit() && !this.extending
            ? html`<d-action plain @click=${() => (this.extending = true)}
                >${t('issues.escalation.snoozeAction')}</d-action
              >`
            : nothing}
        </div>
        ${this.extending ? this.renderExtendForm() : nothing}
        ${extensions.length > 0 ? this.renderDeadlineHistory(extensions) : nothing}
      </div>
    `;
  }

  renderViewMode() {
    return html`
      <div class="status ${this.entityView.processed ? 'processed' : ''}">
        <d-label .field=${this.entityView.processed ? 'issues_processed' : 'issues_processedNOT'}></d-label>
        ${!this.entityView.processed && !this.hideEdit()
          ? html` <d-action plain @click=${() => this.onEditModeOn()}>${t('issues.processIssueAction')}</d-action> `
          : nothing}
      </div>
      ${this.renderFieldSection('issues', this.viewFieldClassification(), this.entityView)}
      <div class="report">${this.renderFieldSection('issues', this.viewFieldsReport(), this.entityView)}</div>
      ${this.renderDeadlineSection()} ${this.renderPllReportView()} ${this.renderPllReportView()}
      <div class="process">${this.renderFieldSection('issues', this.viewFieldsProcess(), this.entityView)}</div>
      ${this.renderMessagesSection()}
    `;
  }

  /**
   * Meldinger på et anonymt avvik «i avviket» (dabih-api #4690). Vises kun for
   * anonyme avvik (reportedBy === ''). organizationId utledes fra pdfHref.
   */
  private renderMessagesSection() {
    if (this.entityView.reportedBy !== '') return nothing;
    const match = (this.entityView.pdfHref ?? '').match(/\/organizations\/([^/]+)\//);
    const organizationId = match ? match[1] : '';
    if (organizationId === '') return nothing;
    return html`
      <d-issue-messages-section
        .organizationId=${organizationId}
        .issueId=${this.entityView.uuid}
        ?anonymous=${true}
        ?closed=${this.entityView.processed}
      ></d-issue-messages-section>
    `;
  }

  editFieldsWorkRelatedHealthIssue(item: IssueViewEditItem): SectionField[] {
    return [
      {
        type: 'expansion',
        opened: item.workRelatedHealthIssue,
        resetHiddenFields: true,
        fields: [
          {
            label: t('issues.workRelatedHealthIssueHeader'),
            type: 'header',
          },
          {
            field: 'eventDate',
            type: 'edit-date',
          },
          {
            field: '',
            label: t('issues.typeLabel'),
            type: 'edit-checkboxes',
            options: [
              { value: 'workRelatedInjury', text: t('issues.typeInjury') },
              { value: 'workRelatedSickness', text: t('issues.typeSickness') },
            ],
            storedAsOptions: true,
            class: 'minWidth300',
          },
          {
            field: 'workRelatedHealthIssueEmployee',
            type: 'edit-dropdown',
            options: this.entityView.workRelatedHealthIssueEmployeeOptions,
            placeholder: t('reports.selectPerson'),
            class: 'minWidth300',
          },
          {
            field: 'workRelatedHealthIssueDescription',
            type: 'edit-textarea',
            class: 'fullWidth',
          },
        ],
      },
      {
        type: 'expansion',
        opened: item.workRelatedInjury,
        resetHiddenFields: true,
        fields: [
          {
            field: 'workRelatedInjuryTask',
            type: 'edit-textarea',
            class: 'fullWidth',
          },
          {
            field: 'workRelatedInjuryIncidentCause',
            type: 'edit-textarea',
            class: 'fullWidth',
          },
        ],
      },
      {
        type: 'expansion',
        opened: item.workRelatedSickness,
        resetHiddenFields: true,
        fields: [
          {
            field: 'workRelatedSicknessCausingTask',
            type: 'edit-textarea',
            class: 'fullWidth',
          },
          {
            field: 'workRelatedSicknessCausingTaskLocation',
            type: 'edit-textarea',
            class: 'fullWidth',
          },
          {
            field: 'workRelatedSicknessCausingEnvironment',
            type: 'edit-textarea',
            class: 'fullWidth',
          },
        ],
      },
    ];
  }

  editFieldsMeasures(item: IssueViewEditItem): SectionField[] {
    return [
      {
        field: '',
        label: t('issues.measuresHeader'),
        type: 'header',
      },
      {
        field: 'requiresMeasures',
        type: 'edit-radio',
        options: [
          { value: 'true', text: t('common:yes') },
          { value: 'false', text: t('common:no') },
        ],
        valueType: 'boolean',
        class: 'fullWidth',
        inlineLabel: true,
      },
      {
        type: 'expansion',
        opened: item.requiresMeasures === true,
        resetHiddenFields: true,
        fields: [
          {
            field: 'identifiedMeasures',
            type: 'edit-textarea',
            class: 'fullWidth',
          },
          {
            type: 'expansion',
            opened: !isEmptyOrInvalidString(item.identifiedMeasures),
            fields: [
              {
                field: 'implementedMeasure',
                type: 'edit-radio',
                options: [
                  { value: 'true', text: t('common:yes') },
                  { value: 'false', text: t('common:no') },
                ],
                valueType: 'boolean',
                class: 'fullWidth',
                inlineLabel: true,
              },
              {
                type: 'expansion',
                opened: item.implementedMeasure === true,
                resetHiddenFields: true,
                fields: [
                  {
                    field: 'evaluated',
                    type: 'edit-radio',
                    options: [
                      { value: 'true', text: t('common:yes') },
                      { value: 'false', text: t('common:no') },
                    ],
                    valueType: 'boolean',
                    class: 'fullWidth',
                    inlineLabel: true,
                  },
                  {
                    type: 'expansion',
                    opened: item.evaluated === true,
                    resetHiddenFields: true,
                    fields: [
                      {
                        field: 'evaluatedDate',
                        type: 'edit-date',
                      },
                      {
                        field: 'evaluatedBy',
                        type: 'edit-dropdown',
                        placeholder: t('reports.selectPerson'),
                        class: 'minWidth300',
                        options: this.entityView.evaluatedByOptions,
                      },
                      {
                        field: 'evaluatedOk',
                        type: 'edit-radio',
                        options: [
                          { value: 'true', text: t('common:yes') },
                          { value: 'false', text: t('common:no') },
                        ],
                        valueType: 'boolean',
                        class: 'fullWidth',
                        inlineLabel: true,
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ];
  }

  editFields(item: IssueViewEditItem): SectionField[] {
    return this.editFieldsBasic(item).concat(
      this.editFieldsChecklist(item),
      this.editFieldsWorkRelatedHealthIssue(item),
      this.editFieldsMeasures(item),
    );
  }

  renderEditItem(item: IssueViewEditItem): TemplateResult<1> {
    return html`
      ${this.renderEditClassification(item)}
      <div class="report">${this.renderFieldSection('issues', this.viewFieldsReport(true), this.entityView)}</div>
      <div class="process">${this.renderFieldSection('issues', this.editFields(item), item)}</div>
    `;
  }

  protected viewFieldsProcess(): SectionField[] {
    return this.viewFieldsProcessAnalysis().concat(this.viewFieldsProcessMeasures());
  }

  protected viewFields(): SectionField[] {
    return this.viewFieldClassification().concat(this.viewFieldsReport(), this.viewFieldsProcess());
  }

  protected lists(): ListDefinition[] {
    return [
      {
        field: 'generalFields_attachments',
        type: 'custom',
        icon: 'attachments',
        items: 'attachments',
        render: () => this.renderAttachments(),
      },
    ];
  }

  protected asUpdateSectionItem(): UpdateSectionItem | undefined {
    return undefined;
  }

  private renderPllReportView() {
    return this.pllParticipant
      ? html` <div class="pllReportField">
          <d-label label="${t('issues.externalReportLabel')}"></d-label>
          <div class="externalReportList">
            ${this._externalReports(this.entityView).map(
              (report) =>
                html` <d-external-report-item
                  created-display="${this._dateFormat(report.created)}"
                  created-display-full="${this.formatAsSentTime(report.created)}"
                  recipient-name="${report.recipientName}"
                  sender-name="${this.capitalize(report.senderName)}"
                  subject="${report.subject}"
                  description="${report.description}"
                ></d-external-report-item>`,
            )}
          </div>
          <div class="pllForm">
            <d-select-dropdown
              placeholder="${t('issues.selectRecipient')}"
              label=""
              .options="${this.externalRecipients}"
              value="${this.externalRecipient}"
            ></d-select-dropdown>
            <button @click=${(e) => this._sendExternalReport(e)} ?disabled=${!this.externalRecipient}>
              ${t('issues.sendExternalReport')}
            </button>
          </div>
        </div>`
      : nothing;
  }

  private editFieldsBasic(item: IssueViewEditItem): SectionField[] {
    return [
      {
        field: 'process',
        type: 'header',
        size: 'verybig',
      },
      {
        field: 'name',
        type: 'edit-text',
        placeholder: item.classification !== 'NONE' ? t('issues.namePublicWarning') : '',
        class: 'fullWidth',
      },
      {
        field: 'processedDate',
        type: 'edit-date',
        class: 'minWidth200',
      },
      {
        field: 'processedBy',
        type: 'edit-dropdown',
        class: 'minWidth300',
        options: this.entityView.processedByOptions,
        placeholder: t('reports.selectPerson'),
      },
      {
        field: 'notes',
        type: 'edit-textarea',
        class: 'fullWidth',
      },
    ];
  }

  private editFieldsChecklist(item: IssueViewEditItem): SectionField[] {
    return [
      {
        field: 'checklist',
        type: 'header',
      },
      {
        field: 'personDamagePossibility',
        optionField: 'personDamagePossibility',
        type: 'edit-checkbox',
        class: 'fullWidth',
      },
      {
        type: 'expansion',
        indent: 20,
        opened: item.personDamagePossibility,
        resetHiddenFields: true,
        fields: [
          {
            field: 'relateToPatient',
            optionField: 'relateToPatient',
            type: 'edit-checkbox',
            class: 'fullWidth',
            hideOptionTooltip: true,
            condition: this.entityView.sector === 'HEALTH',
          },
          {
            type: 'expansion',
            indent: 20,
            condition: true,
            opened: item.relateToPatient,
            resetHiddenFields: true,
            fields: [
              {
                field: 'relateToPatient',
                type: 'view-info',
                class: 'fullWidth',
              },
            ],
          },
          {
            field: 'relateToEmployees',
            optionField: 'relateToEmployees',
            type: 'edit-checkbox',
            class: 'fullWidth',
            hideOptionTooltip: true,
          },
          {
            type: 'expansion',
            indent: 20,
            condition: true,
            opened: item.relateToEmployees,
            resetHiddenFields: true,
            fields: [
              {
                field: 'relateToEmployees',
                type: 'view-info',
                class: 'fullWidth',
              },
              {
                field: 'workRelatedHealthIssue',
                optionField: 'workRelatedHealthIssue',
                type: 'edit-checkbox',
                class: 'fullWidth',
                hideOptionTooltip: true,
              },
              {
                type: 'expansion',
                indent: 20,
                condition: true,
                opened: item.workRelatedHealthIssue,
                fields: [
                  {
                    field: 'workRelatedHealthIssue',
                    type: 'view-info',
                    class: 'fullWidth',
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        field: 'relateToEquipment',
        optionField: 'relateToEquipment',
        type: 'edit-checkbox',
        class: 'fullWidth',
        hideOptionTooltip: true,
        condition: this.entityView.sector === 'HEALTH',
      },
      {
        type: 'expansion',
        indent: 20,
        opened: item.relateToEquipment,
        resetHiddenFields: true,
        fields: [
          {
            field: 'relateToEquipment',
            type: 'view-info',
            class: 'fullWidth',
          },
        ],
      },
      {
        field: 'relateToRadiation',
        optionField: 'relateToRadiation',
        type: 'edit-checkbox',
        class: 'fullWidth',
        hideOptionTooltip: true,
        condition: this.entityView.sector === 'HEALTH',
      },
      {
        type: 'expansion',
        indent: 20,
        opened: item.relateToRadiation,
        resetHiddenFields: true,
        fields: [
          {
            field: 'relateToRadiation',
            type: 'view-info',
            class: 'fullWidth',
          },
        ],
      },
      {
        field: 'informationLeakagePossibility',
        optionField: 'informationLeakagePossibility',
        type: 'edit-checkbox',
        class: 'fullWidth',
        hideOptionTooltip: true,
      },
      {
        type: 'expansion',
        indent: 20,
        condition: true,
        opened: item.informationLeakagePossibility,
        resetHiddenFields: true,
        fields: [
          {
            field: 'informationLeakagePossibility',
            type: 'view-info',
            class: 'fullWidth',
          },
          {
            field: 'informationLeakagePossibilityHEALTH',
            type: 'view-info',
            class: 'fullWidth',
            condition: this.entityView.sector === 'HEALTH',
          },
        ],
      },
      {
        field: 'relateToMaritimeHealthCertificate',
        optionField: 'relateToMaritimeHealthCertificate',
        type: 'edit-checkbox',
        class: 'fullWidth',
        hideOptionTooltip: true,
        condition: this.entityView.maritimeDoctor,
      },
      {
        type: 'expansion',
        indent: 20,
        opened: item.relateToMaritimeHealthCertificate,
        resetHiddenFields: true,
        condition: this.entityView.maritimeDoctor,
        fields: [
          {
            field: 'relateToMaritimeHealthCertificate',
            type: 'view-info',
            class: 'fullWidth',
          },
        ],
      },
      {
        field: 'relateToArtificialIntelligence',
        optionField: 'zz',
        optionLabel: t('issues.aiRelatedCheckboxLabel'),
        type: 'edit-checkbox',
        class: 'fullWidth',
        hideOptionTooltip: true,
        condition: true,
      },
    ];
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-issue-view': DIssueView;
  }
}
